import styled from 'styled-components';
import LLMPlayground from '../../LLMPlayground';
import { Label, NonCopyableMarkdown, SectionWrapper } from '../common';
import { useMutation } from 'react-query';
import { getAssessmentTest, submitLLMTestPrompt } from '../../../api/teams';
import { useParams } from 'react-router-dom';
import useAuth from '../../../hooks/use-auth';
import React from 'react';
import {
  AssessmentTestContext,
  AssessmentTestContextType,
} from '../../../contexts/assessment-test';
import { useLLMPlaygroundValidationPoller } from '../../../hooks/assessment-test/use-llm-validation-polling';
import { useActiveTestStep } from '../../../hooks/assessment-test/use-active-step';
import FollowUp from './FollowUp';

const PlaygroundWrapper = styled.div`
  height: 78vh;
  margin-top: 24px;
  padding-bottom: 12px;

  .llm-playground-wrapper {
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.bgActive};
    background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  }

  .llm-playground-prompt-input-wrapper {
    overflow: hidden;
  }

  .llm-playground-controls {
    padding: 10px 20px;
  }

  .llm-playground-prompt-input {
    textarea {
      min-height: 65vh;
    }
  }
`;

export default function LLMPlaygroundSection({
  section,
  stepId,
}: {
  section: Extract<
    AssessmentTestContextType['steps'][number]['sections'][number],
    { type: 'llm-playground' }
  >;
  stepId: string;
}) {
  const { getTokenSilently } = useAuth();
  const [state, dispatch] = React.useContext(AssessmentTestContext);

  const companyId = state.companyId;
  const testSessionId = state.testSessionId;
  const testId = state.testId;

  const { sectionId } = useActiveTestStep();

  const { companySlug, testSlug } =
    useParams<{ companySlug: string; testSlug: string }>();
  const { content } = section;
  const { prompt, output } = section.answer;

  const onReset = () => {
    dispatch({
      type: 'CHANGE_ANSWER_LLM_PLAYGROUND',
      payload: {
        stepId,
        sectionId: section.id,
        answer: { prompt: section.initialPrompt, output: '' },
      },
    });
  };

  const onPromptChange = (prompt: string) => {
    dispatch({
      type: 'CHANGE_ANSWER_LLM_PLAYGROUND',
      payload: {
        stepId,
        sectionId: section.id,
        answer: { prompt, output: section.answer.output },
      },
    });
  };

  const onOutput = (output: string) => {
    dispatch({
      type: 'CHANGE_ANSWER_LLM_PLAYGROUND',
      payload: {
        stepId,
        sectionId: section.id,
        answer: { prompt: section.answer.prompt, output },
      },
    });
  };

  const fetchTest = React.useCallback(async () => {
    const token = await getTokenSilently();
    const test = await getAssessmentTest(token, {
      companySlug,
      testSlug,
    });
    return test;
  }, [companySlug, testSlug, getTokenSilently]);

  const submitPrompt = React.useCallback(
    async (prompt: string) => {
      const token = await getTokenSilently();
      await submitLLMTestPrompt(token, {
        companyId,
        stepId,
        testSessionId,
        testId,
        sectionId: sectionId ?? '', // TODO: we init sectionId to null. how do we make this safe?
        prompt,
      });
    },
    [companyId, stepId, testSessionId, testId, sectionId, getTokenSilently]
  );

  const { pollTestData, isPolling, error } = useLLMPlaygroundValidationPoller({
    getTestData: fetchTest,
    stepId: stepId,
    sectionId: sectionId ?? '', // TODO: we init sectionId to null. how do we make this safe?
    onOutput,
  });

  const submitMutation = useMutation({
    mutationFn: async (prompt: string) => {
      await submitPrompt(prompt);
      await pollTestData();
    },
  });

  return (
    <SectionWrapper>
      <NonCopyableMarkdown md={content} />
      <PlaygroundWrapper>
        <LLMPlayground
          userPrompt={prompt}
          onPromptChange={onPromptChange}
          result={output}
          review={null}
          onSubmitPrompt={() => {
            submitMutation.mutate(prompt);
          }}
          onReset={onReset}
          isLoading={submitMutation.isLoading || isPolling}
          validationError={
            submitMutation.error || error
              ? 'Something went wrong evaluating your prompt'
              : null
          }
          shouldShowReview={false}
          shouldRunInsteadOfSubmit
          onPaste={(e) => {
            e.preventDefault();
            return false;
          }}
        />
      </PlaygroundWrapper>
      <Label>Write your prompt in the text editor and submit it.</Label>
      <FollowUp section={section} stepId={stepId} />
    </SectionWrapper>
  );
}
